import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  SettingsButton,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import "@NP-Webber/crm-kit/dist/tablekit/TableKit.css";
import {
  cashLedgerApi,
  formatShekels,
  type AccountingDocument,
  type BankLine,
  type FinanceDocument,
  type FinanceReceipt,
  type FinanceSupplier,
  type PaymentRequest,
} from "../../api/cashLedgerApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { CashFileImportDialog } from "./CashFileImportDialog";
import {
  loadAcrossCustomers,
  shekelsToAgorot,
  type CashSectionBaseProps,
  type Scoped,
} from "./cashLedgerUtils";

function useScope(props: CashSectionBaseProps) {
  const showAll = props.customerId === null;
  const scopeIds = useMemo(
    () => (props.customerId ? [props.customerId] : props.customerIds),
    [props.customerId, props.customerIds],
  );
  const writeTargetId = props.customerId ?? props.writeCustomerId;
  const customerLabel = (id: string) => props.customerNames[id] ?? "—";
  return { showAll, scopeIds, writeTargetId, customerLabel };
}

export function SuppliersSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<FinanceSupplier>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listSuppliers(id)));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="ספק חדש"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "name", title: "שם", sortable: true, filterable: true, minWidth: 160 },
            { key: "phone", title: "טלפון", minWidth: 120 },
          ]}
          data={rows.map((s) => ({
            id: `${s.scopeCustomerId}:${s.id}`,
            customer: customerLabel(s.scopeCustomerId),
            name: s.name,
            phone: s.phone ?? "—",
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין ספקים."
          exportFileName="suppliers"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="sm">
        <FormCard
          title="ספק חדש"
          error={createError}
          onSave={() => {
            void (async () => {
              if (!name.trim()) return;
              setBusy(true);
              setCreateError(null);
              try {
                await cashLedgerApi.createSupplier(writeTargetId, { name: name.trim() });
                setDialogOpen(false);
                setName("");
                await load();
              } catch (err) {
                setCreateError(toClientApiError(err).message);
              } finally {
                setBusy(false);
              }
            })();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="הוספה"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={busy || !name.trim()}
        >
          <FormSection>
            <FormGrid>
              <FormField label="שם ספק" required value={name} onChange={setName} />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}

export function PaymentRequestsSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<PaymentRequest>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(
        await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listPaymentRequests(id)),
      );
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="דרישה חדשה"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "title", title: "כותרת", sortable: true, filterable: true, minWidth: 140 },
            { key: "amount", title: "סכום", minWidth: 100 },
            { key: "status", title: "סטטוס", minWidth: 100 },
            {
              key: "actions",
              title: "פעולות",
              minWidth: 140,
              render: (_v, row) => {
                const req = rows.find(
                  (r) => `${r.scopeCustomerId}:${r.id}` === String(row.id),
                );
                if (!req || req.status === "converted" || props.archived) return "—";
                return (
                  <TableRowActions>
                    <QuietButton
                      type="button"
                      onClick={() => {
                        void (async () => {
                          try {
                            await cashLedgerApi.convertPaymentRequest(
                              req.scopeCustomerId,
                              req.id,
                            );
                            await load();
                          } catch (err) {
                            setError(toClientApiError(err).message);
                          }
                        })();
                      }}
                    >
                      המר להוצאה
                    </QuietButton>
                  </TableRowActions>
                );
              },
            },
          ]}
          data={rows.map((r) => ({
            id: `${r.scopeCustomerId}:${r.id}`,
            customer: customerLabel(r.scopeCustomerId),
            title: r.title,
            amount: formatShekels(r.amountAgorot),
            status: r.status,
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין דרישות תשלום."
          exportFileName="payment-requests"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="sm">
        <FormCard
          title="דרישת תשלום חדשה"
          error={createError}
          onSave={() => {
            void (async () => {
              const amountAgorot = shekelsToAgorot(amount);
              if (!title.trim() || amountAgorot === null) {
                setCreateError("נא למלא כותרת וסכום תקין");
                return;
              }
              setBusy(true);
              setCreateError(null);
              try {
                await cashLedgerApi.createPaymentRequest(writeTargetId, {
                  title: title.trim(),
                  amountAgorot,
                });
                setDialogOpen(false);
                setTitle("");
                setAmount("");
                await load();
              } catch (err) {
                setCreateError(toClientApiError(err).message);
              } finally {
                setBusy(false);
              }
            })();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="יצירה"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={busy || !title.trim()}
        >
          <FormSection>
            <FormGrid>
              <FormField label="כותרת" required value={title} onChange={setTitle} />
              <FormField label="סכום (₪)" required value={amount} onChange={setAmount} />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}

export function DocumentsSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<FinanceDocument>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listDocuments(id)));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="מסמך חדש"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "title", title: "כותרת", sortable: true, filterable: true, minWidth: 160 },
            { key: "source", title: "מקור", minWidth: 100 },
          ]}
          data={rows.map((d) => ({
            id: `${d.scopeCustomerId}:${d.id}`,
            customer: customerLabel(d.scopeCustomerId),
            title: d.title,
            source: d.source,
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין מסמכים."
          exportFileName="finance-documents"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="sm">
        <FormCard
          title="מסמך חדש"
          error={createError}
          onSave={() => {
            void (async () => {
              if (!title.trim()) return;
              setBusy(true);
              try {
                await cashLedgerApi.createDocument(writeTargetId, { title: title.trim() });
                setDialogOpen(false);
                setTitle("");
                await load();
              } catch (err) {
                setCreateError(toClientApiError(err).message);
              } finally {
                setBusy(false);
              }
            })();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="הוספה"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={busy || !title.trim()}
        >
          <FormSection>
            <FormGrid>
              <FormField label="כותרת מסמך" required value={title} onChange={setTitle} />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}

export function ReceiptsSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<FinanceReceipt>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [issueViaEzcount, setIssueViaEzcount] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listReceipts(id)));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="קבלה חדשה"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "title", title: "כותרת", sortable: true, filterable: true, minWidth: 160 },
            { key: "amount", title: "סכום", minWidth: 100 },
            { key: "source", title: "מקור", minWidth: 100 },
          ]}
          data={rows.map((r) => ({
            id: `${r.scopeCustomerId}:${r.id}`,
            customer: customerLabel(r.scopeCustomerId),
            title: r.title,
            amount: r.amountAgorot != null ? formatShekels(r.amountAgorot) : "—",
            source: r.source,
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין קבלות."
          exportFileName="receipts"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="sm">
        <FormCard
          title="קבלה חדשה"
          error={createError}
          onSave={() => {
            void (async () => {
              if (!title.trim()) return;
              setBusy(true);
              try {
                await cashLedgerApi.createReceipt(writeTargetId, {
                  title: title.trim(),
                  amountAgorot: shekelsToAgorot(amount),
                  issueViaEzcount,
                });
                setDialogOpen(false);
                setTitle("");
                setAmount("");
                setIssueViaEzcount(false);
                await load();
              } catch (err) {
                setCreateError(toClientApiError(err).message);
              } finally {
                setBusy(false);
              }
            })();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="הוספה"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={busy || !title.trim()}
        >
          <FormSection>
            <FormGrid>
              <FormField label="כותרת" required value={title} onChange={setTitle} />
              <FormField label="סכום (₪)" value={amount} onChange={setAmount} />
              <FormField
                checkbox
                label="הנפק באיזיקאונט"
                checked={issueViaEzcount}
                onChange={
                  ((v: boolean) => setIssueViaEzcount(v)) as unknown as (
                    value: string,
                  ) => void
                }
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}

export function BankSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<BankLine>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listBankLines(id)));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        secondary={
          <SettingsButton
            type="button"
            variant="settings"
            size="sm"
            disabled={props.archived}
            onClick={() => setImportOpen(true)}
          >
            ייבוא מקובץ
          </SettingsButton>
        }
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "date", title: "תאריך", minWidth: 110 },
            {
              key: "description",
              title: "תיאור",
              sortable: true,
              filterable: true,
              minWidth: 160,
            },
            { key: "amount", title: "סכום", minWidth: 100 },
            { key: "status", title: "סטטוס", minWidth: 100 },
            {
              key: "actions",
              title: "פעולות",
              minWidth: 160,
              render: (_v, row) => {
                const line = rows.find(
                  (r) => `${r.scopeCustomerId}:${r.id}` === String(row.id),
                );
                if (!line || line.status !== "unmatched" || props.archived) return "—";
                return (
                  <TableRowActions>
                    <QuietButton
                      type="button"
                      onClick={() => {
                        void (async () => {
                          try {
                            await cashLedgerApi.matchBankLine(
                              line.scopeCustomerId,
                              line.id,
                              "matched",
                            );
                            await load();
                          } catch (err) {
                            setError(toClientApiError(err).message);
                          }
                        })();
                      }}
                    >
                      התאם
                    </QuietButton>
                    <QuietButton
                      type="button"
                      onClick={() => {
                        void (async () => {
                          try {
                            await cashLedgerApi.matchBankLine(
                              line.scopeCustomerId,
                              line.id,
                              "ignored",
                            );
                            await load();
                          } catch (err) {
                            setError(toClientApiError(err).message);
                          }
                        })();
                      }}
                    >
                      התעלם
                    </QuietButton>
                  </TableRowActions>
                );
              },
            },
          ]}
          data={rows.map((line) => ({
            id: `${line.scopeCustomerId}:${line.id}`,
            customer: customerLabel(line.scopeCustomerId),
            date: new Date(line.statementDate).toLocaleDateString("he-IL"),
            description: line.description,
            amount: formatShekels(line.amountAgorot),
            status: line.status,
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין שורות בנק."
          exportFileName="bank-lines"
          showExport
          showFilters
          showColumnPicker
        />
      </div>

      <CashFileImportDialog<
        Awaited<ReturnType<typeof cashLedgerApi.previewBankImportFile>>
      >
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="ייבוא שורות בנק מקובץ"
        recommendImportType="bank"
        hint="עמודות: תיאור/description או כותרת, סכום/amount (שלילי=חיוב), תאריך/date"
        sampleColumns={[
          { key: "description", label: "תיאור" },
          { key: "amountAgorot", label: "סכום (אגורות)" },
          { key: "statementDate", label: "תאריך" },
        ]}
        onPreview={(file) => cashLedgerApi.previewBankImportFile(writeTargetId, file)}
        onCommit={(preview) =>
          cashLedgerApi.commitBankImportFile(writeTargetId, preview.lines)
        }
        onDone={() => void load()}
      />
    </div>
  );
}

export function AccountingSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<AccountingDocument>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [importUrlOrUuid, setImportUrlOrUuid] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const batches = await Promise.all(
        scopeIds.map(async (id) => {
          const acct = await cashLedgerApi.listAccountingDocuments(id);
          return acct.items.map((item) => ({ ...item, scopeCustomerId: id }));
        }),
      );
      setRows(batches.flat());
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="ייבוא / רישום מסמך"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
      />
      <FormAlert severity="info">
        מסמכי הנה״ח דרך איזיקאונט — חיבור אחד ברמת הארגון (הגדרות). ניתן לייבא מסמך
        לפי UUID או קישור ציבורי, או לרשום כותרת מקומית.
      </FormAlert>
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "לקוח", sortable: true, minWidth: 140 }]
              : []),
            { key: "title", title: "כותרת", sortable: true, filterable: true, minWidth: 160 },
            { key: "type", title: "סוג", minWidth: 100 },
            { key: "externalId", title: "מזהה חיצוני", minWidth: 140 },
            {
              key: "pdf",
              title: "PDF",
              minWidth: 80,
              render: (_v, row) => {
                const doc = rows.find(
                  (r) => `${r.scopeCustomerId}:${r.id}` === String(row.id),
                );
                if (!doc?.pdfUrl) return "—";
                return (
                  <a href={doc.pdfUrl} target="_blank" rel="noreferrer">
                    פתח
                  </a>
                );
              },
            },
          ]}
          data={rows.map((d) => ({
            id: `${d.scopeCustomerId}:${d.id}`,
            customer: customerLabel(d.scopeCustomerId),
            title: d.title,
            type: d.documentType,
            externalId: d.externalId ?? "—",
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין מסמכי הנה״ח."
          exportFileName="accounting-documents"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="sm">
        <FormCard
          title="מסמך הנה״ח"
          error={createError}
          onSave={() => {
            void (async () => {
              if (!title.trim()) return;
              setBusy(true);
              try {
                await cashLedgerApi.createAccountingDocument(writeTargetId, {
                  title: title.trim(),
                  importUrlOrUuid: importUrlOrUuid.trim() || undefined,
                });
                setDialogOpen(false);
                setTitle("");
                setImportUrlOrUuid("");
                await load();
              } catch (err) {
                setCreateError(toClientApiError(err).message);
              } finally {
                setBusy(false);
              }
            })();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={busy || !title.trim()}
        >
          <FormSection>
            <FormGrid>
              <FormField label="כותרת" required value={title} onChange={setTitle} />
              <FormField
                label="ייבוא מאיזיקאונט (UUID / קישור)"
                value={importUrlOrUuid}
                onChange={setImportUrlOrUuid}
                placeholder="אופציונלי"
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}
